import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Collapse,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Pagination,
  Avatar,
  Snackbar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import {
  Search,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Replay,
  CreditCard,
  CheckCircle,
  ErrorOutline,
} from '@mui/icons-material';
import axios from '../../resources/axios';
import { formatDate } from '../../resources/functions';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import { selectAuth } from '../../redux/slices/authSlice';

interface RefundRecord {
  ts: string;
  amount: number;
  reason: string | null;
  admin?: string;
  adminLogin?: string;
}

interface StripeOrder {
  _id: string;
  user: string;
  order_id?: string;
  status: string;
  amount: number;
  payment_id?: string;
  city?: string;
  createdAt: string;
  updatedAt: string;
  refundHistory: RefundRecord[];
  _receipted?: boolean;
}

interface Pagination {
  limit: number;
  page: number;
  totalPages: number;
  count: number;
}

const REFUND_REASONS = [
  { value: 'requested_by_customer', label: 'Запит клієнта' },
  { value: 'duplicate', label: 'Дублікат' },
  { value: 'fraudulent', label: 'Шахрайство' },
];

const calcRefundedTotal = (refundHistory: RefundRecord[]) =>
  refundHistory.reduce((sum, r) => sum + (r.amount || 0), 0);

const RefundHistoryRow = ({ refunds }: { refunds: RefundRecord[] }) => {
  const theme = useTheme();
  if (!refunds.length) return (
    <Typography variant="caption" color="textSecondary" sx={{ px: 2, py: 1, display: 'block' }}>
      Повернень немає
    </Typography>
  );
  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, mb: 1, display: 'block', color: '#4ECDC4' }}>
        Історія повернень:
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontSize: 11, py: 0.5 }}>Дата</TableCell>
            <TableCell sx={{ fontSize: 11, py: 0.5 }}>Сума</TableCell>
            <TableCell sx={{ fontSize: 11, py: 0.5 }}>Причина</TableCell>
            <TableCell sx={{ fontSize: 11, py: 0.5 }}>Адмін</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {refunds.map((r, i) => (
            <TableRow key={i}>
              <TableCell sx={{ fontSize: 11, py: 0.5 }}>{formatDate(r.ts, { time: true })}</TableCell>
              <TableCell sx={{ fontSize: 11, py: 0.5, fontWeight: 600 }}>
                {r.amount === 0 ? 'Залишок' : `${r.amount}`}
              </TableCell>
              <TableCell sx={{ fontSize: 11, py: 0.5 }}>{r.reason || '—'}</TableCell>
              <TableCell sx={{ fontSize: 11, py: 0.5 }}>{r.adminLogin || r.admin || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const StripeOrderRow = ({
  order,
  onRefundSuccess,
}: {
  order: StripeOrder;
  onRefundSuccess: (orderId: string) => void;
}) => {
  const theme = useTheme();
  const auth = useSelector(selectAuth);
  const isSuperAdmin = (auth.info as any)?.type === 'superAdmin';

  const [expanded, setExpanded] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState('0');
  const [refundReason, setRefundReason] = useState<string>('requested_by_customer');
  const [refunding, setRefunding] = useState(false);
  const [refundError, setRefundError] = useState('');

  const refundedTotal = calcRefundedTotal(order.refundHistory);
  const remaining = order.amount - refundedTotal;
  const fullyRefunded = remaining <= 0;

  const handleRefund = async () => {
    setRefunding(true);
    setRefundError('');
    try {
      await axios.post('/balance/stripe_refund', {
        resultId: order._id,
        amount: parseFloat(refundAmount),
        reason: refundReason,
      });
      setRefundOpen(false);
      onRefundSuccess(order._id);
    } catch (err: any) {
      setRefundError(err.response?.data?.message || 'Помилка повернення');
    } finally {
      setRefunding(false);
    }
  };

  return (
    <>
      <TableRow
        sx={{
          '&:hover': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' },
          cursor: order.refundHistory.length > 0 ? 'pointer' : 'default',
        }}
        onClick={() => order.refundHistory.length > 0 && setExpanded(e => !e)}
      >
        <TableCell sx={{ py: 1.5 }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            {formatDate(order.createdAt, { time: true })}
          </Typography>
        </TableCell>

        <TableCell sx={{ py: 1.5 }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>
            {order.user}
          </Typography>
        </TableCell>

        <TableCell sx={{ py: 1.5 }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>
            {order.payment_id || '—'}
          </Typography>
        </TableCell>

        <TableCell sx={{ py: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#4CAF50' }}>
            {order.amount}
          </Typography>
          {refundedTotal > 0 && (
            <Typography variant="caption" sx={{ color: '#FF9800', display: 'block' }}>
              −{refundedTotal} повернено
            </Typography>
          )}
        </TableCell>

        <TableCell sx={{ py: 1.5 }}>
          <Chip
            label={order.status}
            size="small"
            icon={order.status === 'success' ? <CheckCircle sx={{ fontSize: 14 }} /> : <ErrorOutline sx={{ fontSize: 14 }} />}
            sx={{
              backgroundColor: order.status === 'success' ? 'rgba(76,175,80,0.15)' : 'rgba(244,67,54,0.15)',
              color: order.status === 'success' ? '#4CAF50' : '#F44336',
              fontWeight: 600,
              fontSize: 11,
              borderRadius: '6px',
            }}
          />
        </TableCell>

        <TableCell sx={{ py: 1.5 }}>
          {order.refundHistory.length > 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip
                label={order.refundHistory.length}
                size="small"
                sx={{ height: 20, fontSize: 11, backgroundColor: 'rgba(255,152,0,0.15)', color: '#FF9800' }}
              />
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}>
                {expanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
              </IconButton>
            </Box>
          ) : (
            <Typography variant="caption" color="textSecondary">—</Typography>
          )}
        </TableCell>

        <TableCell sx={{ py: 1.5 }} onClick={(e) => e.stopPropagation()}>
          {isSuperAdmin && (
            <Tooltip title={fullyRefunded ? 'Повністю повернено' : 'Повернути кошти'}>
              <span>
                <IconButton
                  size="small"
                  disabled={fullyRefunded}
                  onClick={() => setRefundOpen(true)}
                  sx={{
                    color: fullyRefunded ? 'text.disabled' : '#4ECDC4',
                    '&:hover': { backgroundColor: 'rgba(78,205,196,0.1)' },
                  }}
                >
                  <Replay fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>

      {order.refundHistory.length > 0 && (
        <TableRow>
          <TableCell colSpan={7} sx={{ py: 0, border: expanded ? undefined : 'none' }}>
            <Collapse in={expanded} unmountOnExit>
              <Box
                sx={{
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(78,205,196,0.04)' : 'rgba(78,205,196,0.04)',
                  borderLeft: '3px solid #4ECDC4',
                  my: 1,
                  borderRadius: 1,
                }}
              >
                <RefundHistoryRow refunds={order.refundHistory} />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}

      <Dialog open={refundOpen} onClose={() => setRefundOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Повернення Stripe</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ p: 2, borderRadius: 2, backgroundColor: 'rgba(78,205,196,0.08)', border: '1px solid rgba(78,205,196,0.3)' }}>
              <Typography variant="caption" color="textSecondary">Payment ID</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{order.payment_id || order._id}</Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>Оригінальна сума</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{order.amount}</Typography>
              {refundedTotal > 0 && (
                <>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>Вже повернено</Typography>
                  <Typography variant="body2" sx={{ color: '#FF9800', fontWeight: 600 }}>{refundedTotal}</Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>Залишок для повернення</Typography>
                  <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 700 }}>{remaining}</Typography>
                </>
              )}
            </Box>

            <TextField
              label="Сума (0 = повний залишок)"
              type="number"
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ min: 0, max: remaining, step: 0.01 }}
              helperText={`Максимум: ${remaining}`}
            />

            <FormControl size="small" fullWidth>
              <InputLabel>Причина</InputLabel>
              <Select
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                label="Причина"
              >
                {REFUND_REASONS.map(r => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {refundError && <Alert severity="error">{refundError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRefundOpen(false)} sx={{ textTransform: 'none' }}>Скасувати</Button>
          <Button
            variant="contained"
            onClick={handleRefund}
            disabled={refunding}
            sx={{ backgroundColor: '#4ECDC4', '&:hover': { backgroundColor: '#3BA99F' }, textTransform: 'none' }}
          >
            {refunding ? <CircularProgress size={18} color="inherit" /> : 'Повернути'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const StripeHistoryTab = () => {
  const theme = useTheme();
  const admins = useSelector(selectAdmins);

  const [orders, setOrders] = useState<StripeOrder[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ limit: 20, page: 1, totalPages: 1, count: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [userInput, setUserInput] = useState('');
  const [successSnack, setSuccessSnack] = useState(false);
  const [page, setPage] = useState(1);

  const fetchOrders = useCallback(async (pageNum: number, userId: string) => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, any> = { page: pageNum, limit: 20 };
      if (userId.trim()) params.user = userId.trim();
      const res = await axios.get('/balance/stripe_orders', { params });
      setOrders(res.data.data.orders || []);
      setPagination(res.data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Помилка завантаження');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders(page, userFilter);
  }, [fetchOrders, page, userFilter]);

  const handleSearch = () => {
    setPage(1);
    setUserFilter(userInput);
  };

  const handleRefundSuccess = (orderId: string) => {
    setSuccessSnack(true);
    fetchOrders(page, userFilter);
  };

  const isDark = theme.palette.mode === 'dark';

  return (
    <Box>
      <Paper
        sx={{
          p: 2,
          mb: 2,
          border: isDark ? 'none' : '1px solid #E0E0E0',
          borderRadius: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <CreditCard sx={{ color: '#4ECDC4', fontSize: 28 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mr: 1 }}>
          Stripe платежі
        </Typography>
        <TextField
          placeholder="Фільтр за User ID..."
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          size="small"
          sx={{ width: 280 }}
        />
        <Button
          variant="contained"
          startIcon={<Search />}
          onClick={handleSearch}
          sx={{ backgroundColor: '#4ECDC4', '&:hover': { backgroundColor: '#3BA99F' }, textTransform: 'none' }}
        >
          Знайти
        </Button>
        {userFilter && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => { setUserInput(''); setUserFilter(''); setPage(1); }}
            sx={{ textTransform: 'none', borderColor: '#4ECDC4', color: '#4ECDC4' }}
          >
            Очистити
          </Button>
        )}
        <Box sx={{ ml: 'auto' }}>
          <Chip
            label={`Всього: ${pagination.count}`}
            size="small"
            sx={{ backgroundColor: 'rgba(78,205,196,0.15)', color: '#4ECDC4', fontWeight: 600 }}
          />
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ borderRadius: 2, border: isDark ? 'none' : '1px solid #E0E0E0' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Дата</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>User ID</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Payment ID</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Сума</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Статус</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Повернення</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress sx={{ color: '#4ECDC4' }} />
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    <CreditCard sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                    <Typography variant="body2">
                      {userFilter ? 'Платежів для цього користувача не знайдено' : 'Немає платежів'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <StripeOrderRow
                  key={order._id}
                  order={order}
                  onRefundSuccess={handleRefundSuccess}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={pagination.totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            sx={{
              '& .MuiPaginationItem-root.Mui-selected': {
                backgroundColor: '#4ECDC4',
                color: '#fff',
              },
            }}
          />
        </Box>
      )}

      <Snackbar
        open={successSnack}
        autoHideDuration={3000}
        onClose={() => setSuccessSnack(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessSnack(false)}>
          Повернення успішно виконано ✅
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StripeHistoryTab;
